import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { CustomerDatabaseStore } from "../../../../store/main/CustomerDatabaseStore";
import ReportFiltersComponent from "../ReportFiltersComponent";
import ReportScreen from "../../../../common/enums/ReportScreens";
import ReportFiltersLoading from "../ReportFiltersLoading";
import { useEffect, useState } from "react";
import { UserInputAcknowledgementStore } from "../../../../store/main/UserInputAcknowledgementStore";
import moment from "moment";
import { ReportFilters } from "../../../../domain/models/report-filters";
import {
  FetchInputAcknowledgementType,
  UserInputAcknowledgementType,
} from "../../../../domain/models/user-input-acknowledgement/user-input-acknowledgement-type";
import AddAcknowledgementModal from "./Modal/AddAcknowledgementModal";

type InputParams = {
  month: string;
  year: number;
  user_id: number;
};

const commonInputTableStyle = {
  border: "1px solid #e0e0e0",
  py: 0.5,
};

const UserInputAcknowledgementDesktop = () => {
  const { userDetails, decodeToken, userRole } = CustomerDatabaseStore();
  const { FetchInputAcknowledgement, FetchInputAcknowledgementState, fetchInputAcknowledgement } =
    UserInputAcknowledgementStore();

  const currentDate = new Date();
  const [month, setMonth] = useState<number>(currentDate.getMonth() + 1);
  const [year, setYear] = useState<number>(currentDate.getFullYear());
  const [inputPosData, setInputPosData] = useState<
    UserInputAcknowledgementType[]
  >([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [selectedRow, setSelectedRow] =
    useState<UserInputAcknowledgementType | null>(null);

  useEffect(() => {
    decodeToken();
  }, []);

  const fetchAndSetInputData = async (
    month: number,
    year: number,
    userId: number,
    setLoading: (loading: boolean) => void,
    setInputPosData: (data: any) => void
  ) => {
    setLoading(true);
    const monthString = month.toString().padStart(2, "0");

    const inputParams: InputParams = {
      month: monthString,
      year,
      user_id: userId,
    };

    try {
      const response = await fetchInputAcknowledgement(inputParams);
      if (response.input_pos) {
        setInputPosData(response.input_pos);
      }
    } catch (error) {
      console.error("Failed to fetch data", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAndSetInputData(
      month,
      year,
      userDetails.id,
      setLoading,
      setInputPosData
    );
  }, [month, year]);

  const filterData = async (filter: ReportFilters) => {
    const month = parseInt(moment(filter.date?.toString()).format("MM"));
    const year = parseInt(moment(filter.date?.toString()).format("YYYY"));
    setMonth(month);
    setYear(year);

    await fetchAndSetInputData(
      month,
      year,
      userDetails.id,
      setLoading,
      setInputPosData
    );
  };

  const refreshData = async () => {
    await fetchAndSetInputData(
      month,
      year,
      userDetails.id,
      setLoading,
      setInputPosData
    );
  };

  const clearData = async () => {
    await fetchAndSetInputData(
      month,
      year,
      userDetails.id,
      setLoading,
      setInputPosData
    );
  };

  const handleOpenModal = (row: UserInputAcknowledgementType) => {
    setSelectedRow(row);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedRow(null);
  };

  return (
    <>
      <Box pl={2} pt={1}>
        <Box display="flex" justifyContent="space-between" gap={1}>
          <Typography variant="h5" fontWeight={550}>
            User Input Acknowledgement
          </Typography>
        </Box>

        <Box mt={2}>
          {Object.keys(userDetails).length > 0 ? (
            <ReportFiltersComponent
              type={ReportScreen.USER_INPUT_ACKNOWLEDGEMENT}
              successCallback={filterData}
              clearSuccessCallBack={clearData}
              userRole={userRole.role}
              userDetails={userDetails}
              gridColumns={24}
              territoryMultiSelect={false}
            />
          ) : (
            <ReportFiltersLoading />
          )}
        </Box>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : inputPosData.length === 0 ? (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          height="80vh"
          textAlign="center"
        >
          <Typography variant="h3" color="text.secondary">
            Input Acknowledge Data is not available for this month.
          </Typography>
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            mt: 3,
            ml: "auto",
            mr: "auto",
            maxHeight: 500,
            overflowY: "auto",
            width: "95%",
          }}
        >
          <Table stickyHeader aria-label="input-pos-table">
            <TableHead>
              <TableRow>
                {[
                  "Product Name",
                  "Month of Use",
                  "Batch No",
                  "Product Type",
                  "Quantity Dispatched",
                  "Quantity Acknowledge",
                  "Actions",
                ].map((header) => (
                  <TableCell
                    key={header}
                    align="center"
                    sx={{
                      fontWeight: "bold",
                      border: "1px solid #ccc",
                      backgroundColor: "#0D71B7",
                      color: "#ffffff",
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                      py: 1,
                    }}
                  >
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {inputPosData.map((row) => (
                <TableRow key={row.id}>
                  <TableCell align="center" sx={commonInputTableStyle}>
                    {row.product_name}
                  </TableCell>
                  <TableCell align="center" sx={commonInputTableStyle}>
                    {row.month_of_use}
                  </TableCell>
                  <TableCell align="center" sx={commonInputTableStyle}>
                    {row.batch_no || "—"}
                  </TableCell>
                  <TableCell align="center" sx={commonInputTableStyle}>
                    {row.product_type}
                  </TableCell>
                  <TableCell align="center" sx={commonInputTableStyle}>
                    {row.quantity_dispatched}
                  </TableCell>
                  <TableCell align="center" sx={commonInputTableStyle}>
                    {row.quantity_received}
                  </TableCell>
                  <TableCell align="center" sx={commonInputTableStyle}>
                    {[null, 0].includes(row.quantity_received) && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleOpenModal(row)}
                      >
                        Action
                      </Button>
                    )}

                    {openModal && selectedRow && (
                      <AddAcknowledgementModal
                        open={openModal}
                        onClose={handleCloseModal}
                        data={selectedRow}
                        onSuccess={refreshData}
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
};

export default UserInputAcknowledgementDesktop;
